import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import useFetchData from '../../../../hooks/UseFetchData'
import Table from '../../../../components/his/invoices/Table'
import PageHeader from '../../../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import * as XLSX from 'xlsx'
import { ColumnGroup } from 'primereact/columngroup'
import { Column } from 'primereact/column'
import { Row as ColumnRow } from 'primereact/row'

import CustomButton from '../../../../shared/CustomButton'
import Filters from '../../../../components/his/doctorSummaryReport/Filters'
import { AmiriRegBase64 } from '../../../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../../../assets/fonts/AmiriB'

const toNumber = value => Number(value) || 0

const buildTotalsRow = (data, t) => {
  const rows = Array.isArray(data) ? data : []

  return rows.reduce(
    (acc, row) => ({
      ...acc,
      Outpatient: acc.Outpatient + toNumber(row?.Outpatient),
      Inpatient: acc.Inpatient + toNumber(row?.Inpatient),
      Emergency: acc.Emergency + toNumber(row?.Emergency),
      Admissions: acc.Admissions + toNumber(row?.Admissions),
      Discharges: acc.Discharges + toNumber(row?.Discharges),
      SaudiPatients: acc.SaudiPatients + toNumber(row?.SaudiPatients),
      NonSaudiPatients: acc.NonSaudiPatients + toNumber(row?.NonSaudiPatients),
      TotalPatients: acc.TotalPatients + toNumber(row?.TotalPatients),
    }),
    {
      Department: t('Total'),
      Outpatient: 0,
      Inpatient: 0,
      Emergency: 0,
      Admissions: 0,
      Discharges: 0,
      SaudiPatients: 0,
      NonSaudiPatients: 0,
      TotalPatients: 0,
      isGroupHeader: true,
    }
  )
}

const getReportRows = (data, t) => {
  const rows = Array.isArray(data) ? [...data] : []
  if (!rows.length) return []
  return [...rows, buildTotalsRow(rows, t)]
}

const columns = t => [
  {
    header: t('Department'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Department}
      </span>
    ),
  },
  {
    header: t('Outpatient'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Outpatient ?? 0}
      </span>
    ),
  },
  {
    header: t('Inpatient'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Inpatient ?? 0}
      </span>
    ),
  },
  {
    header: t('Emergency'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Emergency ?? 0}
      </span>
    ),
  },
  {
    header: t('Admissions'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Admissions ?? 0}
      </span>
    ),
  },
  {
    header: t('Discharges'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Discharges ?? 0}
      </span>
    ),
  },
  {
    header: t('Saudi Patients'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.SaudiPatients ?? 0}
      </span>
    ),
  },
  {
    header: t('Non Saudi Patients'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.NonSaudiPatients ?? 0}
      </span>
    ),
  },
  {
    header: t('Total Patients'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.TotalPatients ?? 0}
      </span>
    ),
  },
]

export const exportInvoicesToExcel = (
  data,
  t,
  fileName = 'daily-patient-census-report.xlsx'
) => {
  const formatted = getReportRows(data, t).map(row => ({
    [t('Department')]: row?.Department ?? '',
    [t('Outpatient')]: row?.Outpatient ?? 0,
    [t('Inpatient')]: row?.Inpatient ?? 0,
    [t('Emergency')]: row?.Emergency ?? 0,
    [t('Admissions')]: row?.Admissions ?? 0,
    [t('Discharges')]: row?.Discharges ?? 0,
    [t('Saudi Patients')]: row?.SaudiPatients ?? 0,
    [t('Non Saudi Patients')]: row?.NonSaudiPatients ?? 0,
    [t('Total Patients')]: row?.TotalPatients ?? 0,
  }))

  const worksheet = XLSX.utils.json_to_sheet(formatted)
  worksheet['!cols'] = new Array(Object.keys(formatted?.[0] || {}).length).fill({
    wch: 20,
  })
  worksheet['!rows'] = new Array(formatted.length).fill({ hpt: 22 })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    t('Daily Patient Census Report')
  )
  XLSX.writeFile(workbook, fileName)
}

export const exportInvoicesToPDF = (
  data,
  t,
  payloadData,
  fileName = 'daily-patient-census-report.pdf'
) => {
  const doc = new jsPDF('landscape')

  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')

  const lineHeight = 4
  const pageWidth = doc.internal.pageSize.getWidth()
  const currentDate = moment().format('dddd Do MMMM, YYYY')
  const startDate = payloadData?.start_date
    ? moment(payloadData.start_date).format('DD/MM/YYYY')
    : '-'
  const endDate = payloadData?.end_date
    ? moment(payloadData.end_date).format('DD/MM/YYYY')
    : '-'
  const reportTitle = t('Daily Patient Census Report')
  const orgName =
    payloadData?.business_en || payloadData?.business_ar || t('Organization')
  const location = payloadData?.address || t('Location')

  let y = 10

  if (payloadData?.logo) {
    doc.addImage(payloadData.logo, 'PNG', 10, 8, 25, 25)
  }

  const leftMargin = payloadData?.logo ? 40 : 14

  doc.setFontSize(12)
  doc.text(orgName, leftMargin, y + 5)
  doc.setFontSize(10)
  doc.text(location, leftMargin, y + lineHeight + 5)

  doc.setFontSize(11)
  doc.text(reportTitle, pageWidth - 14, y, { align: 'right' })
  doc.setFontSize(10)
  doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - 14, y + lineHeight, {
    align: 'right',
  })

  y += lineHeight * 2
  doc.setDrawColor(0)
  doc.setLineWidth(0.1)
  doc.line(14, y, pageWidth - 14, y)
  y += 6

  doc.text(
    `${t('Period')}: ${startDate} ${t('and upto')} ${endDate}`,
    pageWidth - 14,
    y,
    { align: 'right' }
  )

  const headers = [[
    t('Department'),
    t('Outpatient'),
    t('Inpatient'),
    t('Emergency'),
    t('Admissions'),
    t('Discharges'),
    t('Saudi Patients'),
    t('Non Saudi Patients'),
    t('Total Patients'),
  ]]

  const body = getReportRows(data, t).map(row => [
    row?.Department ?? '',
    row?.Outpatient ?? 0,
    row?.Inpatient ?? 0,
    row?.Emergency ?? 0,
    row?.Admissions ?? 0,
    row?.Discharges ?? 0,
    row?.SaudiPatients ?? 0,
    row?.NonSaudiPatients ?? 0,
    row?.TotalPatients ?? 0,
  ])

  autoTable(doc, {
    startY: y + lineHeight * 2,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: 0,
      fontStyle: 'bold',
      font: 'Amiri',
      fontSize: 8,
      lineWidth: 0.05,
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      font: 'Amiri',
      fontSize: 8,
    },
    didParseCell: hookData => {
      const row = body[hookData.row.index]
      if (hookData.section === 'body' && row?.[0] === t('Total')) {
        hookData.cell.styles.fontStyle = 'bold'
        hookData.cell.styles.fillColor = [245, 245, 245]
      }
    },
  })

  doc.save(fileName)
}

const DailyPatientCensusReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useSelector(state => state.auth)
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading,
  } = useFetchData()

  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    'daily_patient_census_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      license_id: '',
    }
  )

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/patient/census', {
      ...payload,
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
    })
  }

  const reportRows = getReportRows(requestData?.data, t)

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow>
        <Column header={t('Department')} />
        <Column header={t('Outpatient')} />
        <Column header={t('Inpatient')} />
        <Column header={t('Emergency')} />
        <Column header={t('Admissions')} />
        <Column header={t('Discharges')} />
        <Column header={t('Saudi Patients')} />
        <Column header={t('Non Saudi Patients')} />
        <Column header={t('Total Patients')} />
      </ColumnRow>
    </ColumnGroup>
  )

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t('Daily Patient Census Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div className="flex items-center w-full justify-between gap-2 mb-2">
        <Filters
          payloadData={payloadData}
          setPayloadData={setPayloadData}
          requestReport={requestReport}
          insturance
        />
        <div className="flex gap-2">
          <CustomButton
            title={t('Export to Excel')}
            buttonType="success"
            icon={FaFileExcel}
            className="w-fit h-[35px]"
            onClick={() => {
              exportInvoicesToExcel(requestData?.data || [], t)
            }}
          />
          <CustomButton
            title={t('Export to PDF')}
            buttonType="danger"
            icon={FaFilePdf}
            className="w-fit h-[35px]"
            onClick={() => {
              exportInvoicesToPDF(requestData?.data || [], t, {
                ...payloadData,
                business_en: auth?.buisnessInfo?.name_en,
                business_ar: auth?.buisnessInfo?.name_ar,
                address: auth?.buisnessInfo?.address,
                logo: auth?.buisnessInfo?.logo,
              })
            }}
          />
        </div>
      </div>

      <Table
        data={reportRows}
        columns={columns}
        loading={loading}
        headerColumnGroup={headerGroup}
        showGridlines
      />
    </div>
  )
}

export default DailyPatientCensusReport
